/**
 * FileList Component
 *
 * Displays table of uploaded files with:
 * - Filename, size, status columns
 * - Delete action per file
 * - Status indicators (Indexing... | Ready)
 * - Empty state
 */

import { useTranslation } from "react-i18next";
import {
  CircleCheckIcon,
  File,
  FileTextIcon,
  FileType2Icon,
  InboxIcon,
  ScrollTextIcon,
  Trash2Icon,
} from "lucide-react";
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { KnowledgeBaseFile } from '@/features/knowledge-base/knowledge-base-client';
import { formatFileSize } from '@/features/knowledge-base/knowledge-base';

interface FileListProps {
  files: KnowledgeBaseFile[];
  onDelete: (fileId: string, filename: string) => void;
}

export function FileList({ files, onDelete }: FileListProps) {
  const { t, i18n } = useTranslation("kb");

  if (files.length === 0) {
    return (
      <div
        className="py-8 text-center text-[var(--muted-foreground)]"
        data-test="file-list-empty"
      >
        <InboxIcon
          className="mx-auto mb-3 h-12 w-12 text-[var(--muted-foreground)]"
          aria-hidden="true"
        />
        <p className="text-sm">{t("fileList.empty.title")}</p>
        <p className="text-xs mt-1">
          {t("fileList.empty.subtitle")}
        </p>
      </div>
    );
  }

  return (
    <Table data-test="file-list">
        <TableHeader>
          <TableRow>
            <TableHead>{t('fileList.table.file')}</TableHead>
            <TableHead>{t('fileList.table.size')}</TableHead>
            <TableHead>{t('fileList.table.dateAdded')}</TableHead>
            <TableHead>{t('fileList.table.status')}</TableHead>
            <TableHead className="text-right">{t('fileList.table.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {files.map((file) => (
            <TableRow key={file.id} data-test="file-item">
              <TableCell>
                <div className="flex items-center space-x-2">
                  <FileIcon mimeType={file.mimeType} />
                  <span
                    className="truncate text-[var(--foreground)]"
                    title={file.filename}
                  >
                    {file.filename}
                  </span>
                </div>
              </TableCell>
              <TableCell>{formatFileSize(file.fileSizeBytes)}</TableCell>
              <TableCell>
                {new Date(file.createdAt).toLocaleDateString(i18n.language)}
              </TableCell>
              <TableCell>
                <StatusBadge status={file.status} />
              </TableCell>
              <TableCell className="text-right">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => onDelete(file.id, file.filename)}
                  className="text-[var(--destructive)]"
                  data-test="delete-file-btn"
                  title={t('fileList.deleteFile', { filename: file.filename })}
                  aria-label={t('fileList.deleteFile', {
                    filename: file.filename,
                  })}
                >
                  <Trash2Icon className="h-5 w-5" aria-hidden="true" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
  );
}

function FileIcon({ mimeType }: { mimeType: string }) {
  const iconClass = "w-5 h-5";

  if (mimeType === "application/pdf") {
    return (
      <FileTextIcon
        className={`${iconClass} text-[var(--muted-foreground)]`}
        aria-hidden="true"
      />
    );
  }

  if (mimeType.includes("wordprocessingml")) {
    return (
      <FileType2Icon
        className={`${iconClass} text-[var(--muted-foreground)]`}
        aria-hidden="true"
      />
    );
  }

  if (mimeType.includes("text")) {
    return (
      <ScrollTextIcon
        className={`${iconClass} text-[var(--muted-foreground)]`}
        aria-hidden="true"
      />
    );
  }

  return (
    <File
      className={`${iconClass} text-[var(--muted-foreground)]`}
      aria-hidden="true"
    />
  );
}

function StatusBadge({ status }: { status: "indexing" | "ready" }) {
  const { t } = useTranslation("kb");

  if (status === "indexing") {
    return (
      <Badge data-status="indexing" variant="warning">
        <Spinner />
        {t("fileList.status.indexing")}
      </Badge>
    );
  }

  return (
    <Badge data-status="ready" variant="success">
      <CircleCheckIcon aria-hidden="true" />
      {t("fileList.status.ready")}
    </Badge>
  );
}
